import React, { useEffect, useState } from "react";
import styles from "./BidToolForm.module.scss";
import BidtoolContainer from "./BidToolContainer";
import InputField from "../../../../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../../../../enums/InputFieldAttributesEnums";
import { Row, Col } from "antd";
import {
  getMinBidRate,
  getMinutePerLoad,
  getHourlyLoadsPossible,
  unitsOptionsDropdown,
  getMultiLoadFuelValues,
  fieldNames,
  fieldUnitConstants,
  truckingUnit,
} from "./constants";
import PercentageList from "./GradientList";
import { useFormContext } from "react-hook-form";
import { bidtoolFieldKeys } from "./constants";
import ConditionalRender from "../../../../../../reusable-components/ConditionalRender";
import CreateJobServices from "../../../../../../services/createJob.services";
import clsx from "clsx";
import { stripCurrencyFormatting } from "../../../../../../Utilities/regex";
import { DOLLAR_PREFIX } from "../../../../../BidToolConfigurationView/constants";
import { formatNumber } from "../../../../../../utils/numberFormatterUtil";

const BidToolForm = ({ isReference = false }: { isReference?: boolean }) => {
  const { watch, getValues, setValue, clearErrors } = useFormContext();
  const TRUCK_MPG_AVG = watch(fieldNames.TRUCK_MPG_AVG);
  const { GetBidToolsData } = CreateJobServices();
  const { data: bidToolData } = GetBidToolsData();
  const truckTypeOption = bidToolData?.data[0]?.baseTruckAssumption?.map(
    (item) => ({ label: item?.truckType, value: item?.truckType }),
  );
  const initialPercentage = watch(bidtoolFieldKeys.initialPercentage);
  const oneWayMiles = watch(bidtoolFieldKeys.oneWayMiles);
  const loadPerDayPerTruck = watch(bidtoolFieldKeys.loadsPerDayTruck);
  const timeBuffer = watch(bidtoolFieldKeys.timeBuffer) / 100;
  const loadingTime = watch(bidtoolFieldKeys.loadingTime);
  const unloadingTime = watch(bidtoolFieldKeys.unloadingTime);
  const deadheadForwardTime = watch(bidtoolFieldKeys.deadheadForwardTime);
  const deadheadBackwardTime = watch(bidtoolFieldKeys.deadheadBackwardTime);
  const deadheadForwardMiles = watch(bidtoolFieldKeys.deadheadForwardMiles);
  const deadheadBackwardMiles = watch(bidtoolFieldKeys.deadheadBackwardMiles);
  const oneWayTime = watch(bidtoolFieldKeys.oneWayTime);
  const texasDieselAvg = stripCurrencyFormatting(
    watch(bidtoolFieldKeys.texasDieselAvg),
  );
  const minutesPerLoad = getMinutePerLoad({
    oneWayTime: oneWayTime,
    loadingTime: loadingTime,
    unloadingTime: unloadingTime,
    timeBuffer: timeBuffer,
  });
  const totalDeadheadMiles =
    Number(deadheadForwardMiles) + Number(deadheadBackwardMiles);
  const totalDeadheadTime =
    (Number(deadheadBackwardTime) + Number(deadheadForwardTime)) *
    (1 + timeBuffer);
  const milesPerLoad = Number(oneWayMiles) * 2;
  const actualMiles = totalDeadheadMiles + milesPerLoad * loadPerDayPerTruck;
  const gallonsUsed = actualMiles / TRUCK_MPG_AVG;
  const fuelMoneyUsed = gallonsUsed * texasDieselAvg;
  const fuelPerRun = fuelMoneyUsed / loadPerDayPerTruck;

  const twelveHoursLoads = getHourlyLoadsPossible({
    totalDeadheadMinutes: totalDeadheadTime ?? 1,
    hour: 12,
    minutesPerLoad: minutesPerLoad,
  });
  const elevenHoursLoads = getHourlyLoadsPossible({
    totalDeadheadMinutes: totalDeadheadTime ?? 1,
    hour: 11,
    minutesPerLoad: minutesPerLoad ?? 1,
  });
  const tenHoursLoads = getHourlyLoadsPossible({
    totalDeadheadMinutes: Number(totalDeadheadTime),
    hour: 10,
    minutesPerLoad: Number(minutesPerLoad),
  });
  const nineHoursLoads = getHourlyLoadsPossible({
    totalDeadheadMinutes: Number(totalDeadheadTime),
    hour: 9,
    minutesPerLoad: Number(minutesPerLoad),
  });
  const eightHoursLoads = getHourlyLoadsPossible({
    totalDeadheadMinutes: Number(totalDeadheadTime),
    hour: 8,
    minutesPerLoad: Number(minutesPerLoad),
  });
  const multiLoadFuelValueInitialPercentage = getMultiLoadFuelValues({
    oneWayTime: Number(oneWayTime),
    oneWayMiles: Number(oneWayMiles),
    loadsPerDayTruck: Number(loadPerDayPerTruck),
    deadheadBackwardMiles: Number(deadheadBackwardMiles),
    deadheadForwardMiles: Number(deadheadForwardMiles),
    texasDieselAvg: Number(texasDieselAvg),
    truckMpgAvg: Number(TRUCK_MPG_AVG),
    truckRate: 4,
    percentage: Number(initialPercentage),
  });

  const truckType = watch("truckType");
  const averageNumberLoads = watch("averageNumberLoads");
  const maxNumberLoads = watch("maxNumberLoads");
  const currentTruckTypeBiddingTons = watch(`${truckType}_biddingTons`);
  const [currentTruckTypeRate, setCurrentTruckTypeRate] = useState(0);
  const minBidValue = getMinBidRate({
    oneWayTime: Number(oneWayTime),
    loadPerDayPerTruck: Number(loadPerDayPerTruck),
    timeBuffer: Number(timeBuffer),
    loadingTime: Number(loadingTime),
    unloadingTime: Number(unloadingTime),
    deadheadBackwardTime: Number(deadheadBackwardTime),
    deadheadForwardTime: Number(deadheadForwardTime),
    truckRate: currentTruckTypeRate,
  });

  const getActualBid = (val) => {
    try {
      const actualVal = parseFloat?.(stripCurrencyFormatting?.(String(val)));
      if (!isNaN(actualVal)) {
        return actualVal?.toFixed(2);
      }
      return 0;
    } catch (err) {
      console.error(err);
    }
  };

  const actualBid = watch("actualBid");
  const truckingPriceFieldValue = watch("truckPrice");
  const truckingCostFieldValue = watch(fieldNames.TRUCKING_COST);
  const truckingPriceUnit = watch("truckingPriceUnit");

  const actualBidPerLoad =
    currentTruckTypeBiddingTons !== undefined && actualBid !== undefined
      ? truckingPriceUnit === truckingUnit.UNIT_LOAD
        ? Number(getActualBid(actualBid))
        : Number(currentTruckTypeBiddingTons) * Number(getActualBid(actualBid))
      : "";

  const perTonValue = Number(minBidValue) / Number(currentTruckTypeBiddingTons);

  const averageRevenue =
    parseFloat(stripCurrencyFormatting(averageNumberLoads)) * actualBidPerLoad;

  const maximumRevenue =
    parseFloat(stripCurrencyFormatting(maxNumberLoads)) * actualBidPerLoad;

  const handleTexasDieselRedirection = () => {
    window.open("https://gasprices.aaa.com/?state=TX", "_blank");
  };

  const handleBlur =
    (fieldName: string) => (event: React.FocusEvent<HTMLInputElement>) => {
      const value = parseFloat(stripCurrencyFormatting(event.target.value));
      if (!isNaN(value)) {
        const roundedValue = value.toFixed(2);
        setValue(fieldName, roundedValue);

        if (fieldName.split("_")[0] === truckType)
          setCurrentTruckTypeRate(value);
      }
    };

  useEffect(() => {
    setValue("averageRevenue", averageRevenue);
  }, [averageNumberLoads, currentTruckTypeBiddingTons, perTonValue]);

  const [truckingCostFieldDisableStatus, setTruckingCostFieldDisabled] =
    useState(true);
  const handleTruckingPriceCost = (isTruckPrice: boolean, markup: number) => {
    const [truckingFieldValue, fieldName, adjacentFieldName] = isTruckPrice
      ? [
          truckingPriceFieldValue,
          fieldNames.TRUCKING_COST,
          fieldNames.TRUCK_PRICE,
        ]
      : [
          truckingCostFieldValue,
          fieldNames.TRUCK_PRICE,
          fieldNames.TRUCKING_COST,
        ];
    const truckingVal = stripCurrencyFormatting(truckingFieldValue);
    if (truckingVal !== undefined && truckingVal !== null) {
      const val = (truckingVal * markup)?.toFixed(2);
      setValue(fieldName, !isNaN(val) ? val : "");
      setValue(adjacentFieldName, truckingVal);
      clearErrors(fieldName);
      clearErrors(adjacentFieldName);
      if (isTruckPrice) {
        setValue(fieldNames.ACTUAL_BID, truckingVal);
        setTruckingCostFieldDisabled(false);
      } else {
        setValue(fieldNames.ACTUAL_BID, val);
      }
    }
  };

  useEffect(() => {
    if (truckTypeOption?.length > 0) {
      setValue(fieldNames.TRUCK_TYPE, truckTypeOption?.[0]?.value);
      setCurrentTruckTypeRate(getValues(`${truckTypeOption?.[0]?.value}_rate`));
    }
  }, [truckTypeOption?.length, setValue, getValues]);

  useEffect(() => {
    if (!isNaN(tenHoursLoads) && tenHoursLoads !== Infinity)
      setValue("loadsPerDayTruck", Math?.floor(tenHoursLoads) ?? 0);
    else setValue("loadsPerDayTruck", null);
  }, [tenHoursLoads]);

  const [onFocusTruckField, setOnFocusTruckField] = useState(null);

  useEffect(() => {
    if (onFocusTruckField === fieldNames.TRUCK_PRICE) {
      handleBlur(fieldNames.TRUCK_PRICE);
      handleTruckingPriceCost(true, 0.875);
    }

    if (onFocusTruckField === fieldNames.TRUCKING_COST) {
      handleBlur(fieldNames.TRUCKING_COST);
      handleTruckingPriceCost(false, 1 / 0.875);
    }
  }, [truckingPriceFieldValue, truckingCostFieldValue]);

  return (
    <div className={styles.BidToolFormContainer}>
      <h1>BASE TRUCK ASSUMPTIONS</h1>
      <Row>
        <Col span={9}>
          <BidtoolContainer
            containerOne
            headerTitles={["Truck types", "Rate Per Hour", "Bidding Tons"]}
            scrollable
          >
            {bidToolData?.data?.[0]?.baseTruckAssumption?.map((field) => (
              <div className={styles.RowContainer}>
                <div>{field.truckType}</div>
                <div>
                  <InputField
                    formattedNumberType
                    name={`${field.truckType}_rate`}
                    type={InputfieldTypes.TEXT}
                    prefix={DOLLAR_PREFIX}
                    onBlur={handleBlur(`${field.truckType}_rate`)}
                  />
                </div>
                <div>
                  <InputField
                    small
                    name={`${field.truckType}_biddingTons`}
                    type={InputfieldTypes.TEXT}
                  />
                </div>
              </div>
            ))}
          </BidtoolContainer>
          <BidtoolContainer headerTitles={["Bid Calculator Inputs"]}>
            <div className={styles.RowContainer}>
              <div>Truck Type</div>
              <div className={styles.truckInputSelector}>
                <InputField
                  name="truckType"
                  customSelect
                  options={truckTypeOption}
                  type={InputfieldTypes.SELECT}
                  onSelect={(val) =>
                    setCurrentTruckTypeRate(getValues(`${val}_rate`))
                  }
                />
              </div>
            </div>
            <div className={styles.RowContainer}>
              <div
                onClick={handleTexasDieselRedirection}
                className={styles.TexasDieselText}
              >
                Texas Diesel Avg
              </div>
              <div>
                <InputField
                  formattedNumberType
                  name={fieldNames.TEXAS_DIESEL_AVG}
                  type={InputfieldTypes.TEXT}
                  prefix={DOLLAR_PREFIX}
                  onBlur={handleBlur(fieldNames.TEXAS_DIESEL_AVG)}
                />
              </div>
              <div className={styles.RowContainerUnits}>gal</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Truck Rate</div>
              <div className={styles.AlignCenter}>
                ${formatNumber(Number(currentTruckTypeRate)?.toFixed(2))}
              </div>
              <div className={styles.RowContainerUnits}>hr</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Truck MPG Avg</div>
              <div
                style={{
                  textAlign: "start",
                }}
              >
                <InputField
                  formattedNumberType
                  name={fieldNames.TRUCK_MPG_AVG}
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>mpg</div>
            </div>
          </BidtoolContainer>
          <BidtoolContainer headerTitles={["Run Information"]}>
            <div className={styles.RowContainer}>
              <div>1-Way Time</div>
              <div>
                <InputField
                  small
                  name="oneWayTime"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>minutes</div>
            </div>
            <div className={styles.RowContainer}>
              <div>1-Way Miles</div>
              <div>
                <InputField
                  small
                  name="oneWayMiles"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>miles</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Loading (Time)</div>
              <div>
                <InputField
                  small
                  name="loadingTime"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>minutes</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Unloading (Time)</div>
              <div>
                <InputField
                  small
                  name="unloadingTime"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>minutes</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Deadhead (F, Time)</div>
              <div>
                <InputField
                  small
                  name="deadheadForwardTime"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>minutes</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Deadhead (F, Miles)</div>
              <div>
                <InputField
                  small
                  name="deadheadForwardMiles"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>miles</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Deadhead (B, Time)</div>
              <div>
                <InputField
                  small
                  name="deadheadBackwardTime"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>minutes</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Deadhead (B, Miles)</div>
              <div>
                <InputField
                  small
                  name="deadheadBackwardMiles"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>miles</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Time Buffer, Semi</div>
              <div>
                <InputField
                  small
                  name="timeBuffer"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div className={styles.RowContainerUnits}>
                {fieldUnitConstants.TIME_BUFFER_UNIT}
              </div>
            </div>
          </BidtoolContainer>
        </Col>
        <Col span={6}>
          <BidtoolContainer small headerTitles={["Multi-Load Fuel Bid Chart"]}>
            <div className={styles.RowContainerFuel}>
              <div>Min. Bid</div>
              <div>
                <ConditionalRender
                  visible={!isNaN(minBidValue) && minBidValue !== Infinity}
                  fallback={<div className={styles.HyphenContainer}>-</div>}
                >
                  ${formatNumber(minBidValue.toFixed(2))}
                </ConditionalRender>
              </div>
            </div>
            <div className={styles.RowContainerFuel}>
              <div>Per Ton</div>
              <ConditionalRender
                visible={!isNaN(perTonValue) && perTonValue !== Infinity}
                fallback={<div className={styles.HyphenContainer}>-</div>}
              >
                <div>${formatNumber(perTonValue?.toFixed(2))}</div>
              </ConditionalRender>
            </div>
            <div className={styles.RowContainerFuel}>
              <div>Loads/ Day/ Truck</div>
              <div>
                <InputField
                  small
                  name="loadsPerDayTruck"
                  type={InputfieldTypes.TEXT}
                />
              </div>
            </div>
            <div className={styles.RowContainerFuelInitailPercent}>
              <div className={styles.InitialPercentage}>
                <InputField
                  small
                  name="initialPercentage"
                  type={InputfieldTypes.TEXT}
                  suffix="%"
                />
              </div>
              <ConditionalRender
                visible={!isNaN(Number(multiLoadFuelValueInitialPercentage))}
                fallback={<div className={styles.HyphenContainer}>-</div>}
              >
                <div
                  className={clsx({
                    [styles.Green]:
                      minBidValue < multiLoadFuelValueInitialPercentage,
                    [styles.Red]:
                      minBidValue > multiLoadFuelValueInitialPercentage,
                  })}
                >
                  <ConditionalRender
                    visible={multiLoadFuelValueInitialPercentage !== Infinity}
                    fallback={<div className={styles.HyphenContainer}>-</div>}
                  >
                    $
                    {formatNumber(
                      multiLoadFuelValueInitialPercentage?.toFixed(2),
                    )}
                  </ConditionalRender>
                </div>
              </ConditionalRender>
            </div>
            <PercentageList
              oneWayMiles={Number(oneWayMiles)}
              oneWayTime={Number(oneWayTime)}
              loadsPerDayTruck={Number(loadPerDayPerTruck)}
              deadheadBackwardMiles={Number(deadheadBackwardMiles)}
              deadheadForwardMiles={Number(deadheadForwardMiles)}
              truckMpgAvg={TRUCK_MPG_AVG}
              texasDieselAvg={Number(texasDieselAvg)}
              minBidValue={minBidValue}
              initialPercentage={Number(initialPercentage)}
              start={Number(initialPercentage)}
              key={initialPercentage}
            />
          </BidtoolContainer>
          <BidtoolContainer small headerTitles={["Run Details"]}>
            <Row justify="space-between" style={{ marginBlock: "0.5rem" }}>
              <Col span={16}>
                <div>Total Deadhead Miles</div>
              </Col>
              <Col span={6}>
                <ConditionalRender
                  visible={
                    !isNaN(totalDeadheadMiles) &&
                    totalDeadheadMiles !== Infinity
                  }
                  fallback={"-"}
                >
                  <div>{totalDeadheadMiles?.toFixed(2)}</div>
                </ConditionalRender>
              </Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: "0.5rem" }}>
              <Col span={16}>
                <div>Total Deadhead Minutes</div>
              </Col>
              <Col span={6}>
                <ConditionalRender
                  visible={
                    !isNaN(totalDeadheadTime) && totalDeadheadTime !== Infinity
                  }
                  fallback={"-"}
                >
                  <div>{totalDeadheadTime?.toFixed(2)}</div>
                </ConditionalRender>
              </Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: "0.5rem" }}>
              <Col span={16}>
                {" "}
                <div>Miles per Load</div>
              </Col>
              <Col span={6}>
                <ConditionalRender
                  visible={!isNaN(milesPerLoad) && milesPerLoad !== Infinity}
                  fallback={"-"}
                >
                  <div>{milesPerLoad?.toFixed(2)}</div>
                </ConditionalRender>
              </Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: "0.5rem" }}>
              <Col span={16}>
                <div>Minutes per Load</div>
              </Col>
              <Col span={6}>
                <ConditionalRender
                  visible={
                    !isNaN(minutesPerLoad) && minutesPerLoad !== Infinity
                  }
                  fallback={"-"}
                >
                  <div>{minutesPerLoad?.toFixed(2)}</div>
                </ConditionalRender>
              </Col>
            </Row>
          </BidtoolContainer>
        </Col>
        <Col span={9}>
          <BidtoolContainer headerTitles={["Truck Price Details"]}>
            <div className={styles.RowContainer}>
              <div>Trucking Price</div>
              <div>
                <InputField
                  formattedNumberType
                  name={fieldNames.TRUCK_PRICE}
                  type={InputfieldTypes.TEXT}
                  placeholder="Price"
                  onFocus={() => setOnFocusTruckField(fieldNames.TRUCK_PRICE)}
                  onBlur={() => setOnFocusTruckField(null)}
                  prefix={DOLLAR_PREFIX}
                  disabled={isReference}
                />
              </div>
              <div>
                <InputField
                  customSelect
                  name="truckingPriceUnit"
                  options={unitsOptionsDropdown}
                  type={InputfieldTypes.SELECT}
                  onSelect={(value) => setValue("truckingCostUnit", value)}
                />
              </div>
            </div>
            <div className={styles.RowContainer}>
              <div>Trucking Cost</div>
              <div>
                <InputField
                  formattedNumberType
                  name={fieldNames.TRUCKING_COST}
                  type={InputfieldTypes.TEXT}
                  placeholder="Price"
                  onFocus={() => setOnFocusTruckField(fieldNames.TRUCKING_COST)}
                  onBlur={() => setOnFocusTruckField(null)}
                  prefix={DOLLAR_PREFIX}
                  disabled={truckingCostFieldDisableStatus}
                />
              </div>
              <div>
                <InputField
                  customSelect
                  options={unitsOptionsDropdown}
                  name="truckingCostUnit"
                  type={InputfieldTypes.SELECT}
                  onSelect={(value) => setValue("truckingPriceUnit", value)}
                />
              </div>
            </div>
          </BidtoolContainer>
          <BidtoolContainer headerTitles={["Loads Possible Per X Drive Hours"]}>
            <div className={styles.RowContainer}>
              <div>12</div>
              <ConditionalRender
                visible={
                  !isNaN(twelveHoursLoads) && twelveHoursLoads !== Infinity
                }
                fallback={"-"}
              >
                <ConditionalRender
                  visible={
                    !isNaN(elevenHoursLoads) && elevenHoursLoads !== Infinity
                  }
                  fallback={"-"}
                >
                  <div>{twelveHoursLoads.toFixed(2)}</div>
                </ConditionalRender>
              </ConditionalRender>
              <div>loads</div>
            </div>
            <div className={styles.RowContainer}>
              <div>11</div>
              <ConditionalRender
                visible={
                  !isNaN(elevenHoursLoads) && elevenHoursLoads !== Infinity
                }
                fallback={"-"}
              >
                <div>{elevenHoursLoads.toFixed(2)}</div>
              </ConditionalRender>
              <div>loads</div>
            </div>
            <div className={styles.RowContainer}>
              <div>10</div>
              <ConditionalRender
                visible={!isNaN(tenHoursLoads) && tenHoursLoads !== Infinity}
                fallback={"-"}
              >
                <div>{tenHoursLoads.toFixed(2)}</div>
              </ConditionalRender>
              <div>loads</div>
            </div>
            <div className={styles.RowContainer}>
              <div>9</div>
              <ConditionalRender
                visible={!isNaN(nineHoursLoads) && nineHoursLoads !== Infinity}
                fallback={"-"}
              >
                <div>{nineHoursLoads.toFixed(2)}</div>
              </ConditionalRender>
              <div>loads</div>
            </div>
            <div className={styles.RowContainer}>
              <div>8</div>
              <ConditionalRender
                visible={
                  !isNaN(eightHoursLoads) && eightHoursLoads !== Infinity
                }
                fallback={"-"}
              >
                <div>{eightHoursLoads?.toFixed(2)}</div>
              </ConditionalRender>
              <div>loads</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Actual miles</div>
              <ConditionalRender
                visible={!isNaN(actualMiles) && actualMiles !== Infinity}
                fallback={"-"}
              >
                <div>{actualMiles?.toFixed(2)}</div>
              </ConditionalRender>
              <div>miles</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Gallon used</div>
              <ConditionalRender
                visible={!isNaN(gallonsUsed) && gallonsUsed !== Infinity}
                fallback={"-"}
              >
                <div>{gallonsUsed?.toFixed(2)}</div>
              </ConditionalRender>
              <div>gallons</div>
            </div>
            <div className={styles.RowContainer}>
              <div>$$$ Fuel used</div>
              <ConditionalRender
                visible={!isNaN(fuelMoneyUsed) && fuelMoneyUsed !== Infinity}
                fallback={"-"}
              >
                <div>${formatNumber(fuelMoneyUsed?.toFixed(2))}</div>
              </ConditionalRender>
              <div>Daily</div>
            </div>
            <div className={styles.RowContainer}>
              <div>Fuel per run</div>
              <ConditionalRender
                visible={!isNaN(fuelPerRun) && fuelPerRun !== Infinity}
                fallback={"-"}
              >
                <div>${formatNumber(fuelPerRun?.toFixed(2))}</div>
              </ConditionalRender>
            </div>
          </BidtoolContainer>
          <BidtoolContainer headerTitles={["Truck Daily Revenue Numbers"]}>
            <div className={styles.RevContainer}>
              <div>Actual Bid</div>
              <div>
                {" "}
                <InputField
                  formattedNumberType
                  name={fieldNames.ACTUAL_BID}
                  type={InputfieldTypes.TEXT}
                  prefix={DOLLAR_PREFIX}
                  onBlur={handleBlur(fieldNames.ACTUAL_BID)}
                />
              </div>
              <div>{`per ${watch("truckingPriceUnit") ?? "ton"}`}</div>
            </div>
            <div className={styles.RevContainer}>
              <div>Actual Bid per Load</div>
              <ConditionalRender
                visible={
                  !isNaN(actualBidPerLoad) &&
                  actualBidPerLoad !== undefined &&
                  actualBidPerLoad !== ""
                }
                fallback={
                  <div className={styles.HyphenContainerTruckDaily}>-</div>
                }
              >
                <div className={styles.valueContainer}>
                  $
                  {formatNumber(
                    actualBidPerLoad !== "" ? actualBidPerLoad.toFixed(2) : "",
                  )}
                </div>
              </ConditionalRender>
            </div>
            <div className={styles.RevContainer}>
              <div>Avg Number of Loads</div>
              <div>
                <InputField
                  formattedNumberType
                  name="averageNumberLoads"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div>loads</div>
            </div>
            <div className={styles.RevContainer}>
              <div>Max Number of Loads</div>
              <div>
                <InputField
                  formattedNumberType
                  name="maxNumberLoads"
                  type={InputfieldTypes.TEXT}
                />
              </div>
              <div>loads</div>
            </div>
            <div className={styles.RevContainer}>
              <div>Average Revenue</div>
              <ConditionalRender
                visible={!isNaN(averageRevenue)}
                fallback={
                  <div className={styles.HyphenContainerTruckDaily}>-</div>
                }
              >
                <div className={styles.valueContainer}>
                  {/* <div className={styles.avgRevenueField}>
                    <InputField
                      small
                      name="averageRevenue"
                      type={InputfieldTypes.TEXT}
                    />
                  </div> */}
                  ${formatNumber(averageRevenue?.toFixed(2))}
                </div>
              </ConditionalRender>
            </div>
            <div className={styles.RevContainer}>
              <div>Maximum Revenue</div>
              <ConditionalRender
                visible={!isNaN(maximumRevenue)}
                fallback={
                  <div className={styles.HyphenContainerTruckDaily}>-</div>
                }
              >
                <div className={styles.valueContainer}>
                  ${formatNumber(maximumRevenue?.toFixed(2))}
                </div>
              </ConditionalRender>
            </div>
          </BidtoolContainer>
        </Col>
      </Row>
    </div>
  );
};

export default BidToolForm;
